import { faFaucet } from '@fortawesome/free-solid-svg-icons/faFaucet';
import { faWarehouse } from '@fortawesome/free-solid-svg-icons/faWarehouse';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import AlarmOffIcon from '@mui/icons-material/AlarmOff';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import HandymanIcon from '@mui/icons-material/Handyman';
import HomeIcon from '@mui/icons-material/Home';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import PoolIcon from '@mui/icons-material/Pool';
import ScheduleIcon from '@mui/icons-material/Schedule';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import React from 'react';

import { OverrideProperties } from 'type-fest';

import {
  CompanyWindowListDTO,
  CompanyWindowLabelDisplayMap,
  CompanyWindowLabelEnum,
  TradeEnum,
  WaivableStatusEnum,
  ServiceTypeEnum,
  Company
} from '_lib/api/apiTypes.shim';

import { Stack } from '_lib/components/Stack/Stack';
import { Tooltip } from '_lib/components/Tooltip/Tooltip';
import { Typography } from '_lib/components/Typography/Typography';

export type CompanyWindowDisplayProps = {
  companyWindow: Pick<
    /* `isActive` is not provided by appointmentHistory item :shrug: */
    OverrideProperties<CompanyWindowListDTO, { isActive?: boolean }>,
    | 'isActive'
    | 'label'
    | 'name'
    | 'isGuaranteed'
    | 'isOnCall'
    | 'isNoTimeFrame'
    | 'isTechAssign'
    | 'isWarranty'
    | 'serviceFee'
    | 'serviceTypes'
    | 'trades'
    | 'waivableStatus'
    | 'zones'
  >;
  isManualEmployeeAssignEnabled: Company['isManualEmployeeAssignEnabled'];
  highlight?: true;
  includeName?: boolean;
  includeTrades?: boolean;
} & (
  | {
      includeZones: boolean;
      company: Company;
    }
  | { includeZones?: never }
);

export const CompanyWindowDisplay = (props: CompanyWindowDisplayProps): React.ReactElement => {
  const { companyWindow, isManualEmployeeAssignEnabled, highlight, includeName, includeTrades, includeZones } = props;

  let zoneNames =
    includeZones && props.company.isZoned
      ? props.company.zones.reduce<string[]>((acc, zone) => {
          if (companyWindow.zones?.includes(zone.id)) acc.push(zone.title);
          return acc;
        }, [])
      : undefined;

  if (includeZones && props.company.zones.length === zoneNames?.length) {
    zoneNames = ['All Zones'];
  }

  return (
    <Stack spacing={0}>
      <Stack direction='row' spacing={0.5}>
        <Typography text={companyWindow.serviceFee ? `$${companyWindow.serviceFee}` : 'Free'} />
        {companyWindow.label !== CompanyWindowLabelEnum.Service && (
          <Typography
            variant={highlight ? 'body1Bold' : undefined}
            text={CompanyWindowLabelDisplayMap[companyWindow.label]}
          />
        )}
        {includeName && <Typography text={` (${companyWindow.name})`} />}
        {companyWindow.label === CompanyWindowLabelEnum.Service && (
          <Typography
            variant={highlight ? 'body1Bold' : undefined}
            text={companyWindow.serviceTypes.reduce((acc, serviceType, index) => {
              acc += ServiceTypeEnum[serviceType];
              if (index < companyWindow.serviceTypes.length - 1) acc += ', ';
              return acc;
            }, '')}
          />
        )}
        {companyWindow.isGuaranteed && (
          <Tooltip title='Guaranteed' placement='bottom'>
            <ScheduleIcon sx={(theme) => ({ color: theme.palette.calendar.guaranteed })} fontSize='small' />
          </Tooltip>
        )}
        {companyWindow.isWarranty && (
          <Tooltip title='Warranty Ok' placement='bottom'>
            <VerifiedUserIcon sx={(theme) => ({ color: theme.palette.calendar.warranty })} fontSize='small' />
          </Tooltip>
        )}
        {companyWindow.isNoTimeFrame && (
          <Tooltip title='No Time Frame' placement='bottom'>
            <AlarmOffIcon sx={(theme) => ({ color: theme.palette.calendar.noTimeFrame })} fontSize='small' />
          </Tooltip>
        )}
        {companyWindow.isOnCall && (
          <Tooltip title='Notify Tech' placement='bottom'>
            <PhoneInTalkIcon sx={(theme) => ({ color: theme.palette.calendar.onCall })} fontSize='small' />
          </Tooltip>
        )}
        {isManualEmployeeAssignEnabled && companyWindow.isTechAssign && (
          <Tooltip title='Assign a Tech' placement='bottom'>
            <AssignmentIndIcon sx={(theme) => ({ color: theme.palette.calendar.techAssign })} fontSize='small' />
          </Tooltip>
        )}
        {companyWindow.waivableStatus === WaivableStatusEnum.Never && (
          <Tooltip title='Never Waive Fee' placement='bottom'>
            <MonetizationOnIcon sx={(theme) => ({ color: theme.palette.calendar.notWaivable })} fontSize='small' />
          </Tooltip>
        )}
        {companyWindow.isActive === false && <Typography component='span' text='INACTIVE' color='error' />}
      </Stack>
      <Stack direction='row' spacing={0.5}>
        {companyWindow.label !== CompanyWindowLabelEnum.Service && (
          <Typography
            text={companyWindow.serviceTypes.reduce((acc, serviceType, index) => {
              acc += ServiceTypeEnum[serviceType];
              if (index < companyWindow.serviceTypes.length - 1) acc += ', ';
              return acc;
            }, '')}
          />
        )}
        {includeZones && zoneNames && zoneNames?.length > 0 && <Typography text={`(${zoneNames.join(', ')})`} />}
      </Stack>
      {includeTrades && (
        <Stack direction='row' spacing={0.5} sx={{ opacity: 0.75 }}>
          <Typography text='Trades: ' />
          {companyWindow.trades.includes(TradeEnum.Electric) && (
            <Tooltip title='Electric' placement='bottom'>
              <FlashOnIcon fontSize='small' />
            </Tooltip>
          )}
          {companyWindow.trades.includes(TradeEnum.HVAC) && (
            <Tooltip title='HVAC' placement='bottom'>
              <AcUnitIcon fontSize='small' />
            </Tooltip>
          )}
          {companyWindow.trades.includes(TradeEnum.Plumbing) && (
            <Tooltip title='Plumbing' placement='bottom'>
              <span>
                <FontAwesomeIcon icon={faFaucet} />
              </span>
            </Tooltip>
          )}
          {companyWindow.trades.includes(TradeEnum.Roofing) && (
            <Tooltip title='Roofing' placement='bottom'>
              <HomeIcon fontSize='small' />
            </Tooltip>
          )}
          {companyWindow.trades.includes(TradeEnum.Restoration) && (
            <Tooltip title='Restoration' placement='bottom'>
              <HandymanIcon fontSize='small' />
            </Tooltip>
          )}
          {companyWindow.trades.includes(TradeEnum.Garage) && (
            <Tooltip title='Garage' placement='bottom'>
              <span>
                <FontAwesomeIcon icon={faWarehouse} />
              </span>
            </Tooltip>
          )}
          {companyWindow.trades.includes(TradeEnum.Pools) && (
            <Tooltip title='Pools' placement='bottom'>
              <PoolIcon fontSize='small' />
            </Tooltip>
          )}
        </Stack>
      )}
    </Stack>
  );
};
